<template>
  <div v-loading="isLoading">
    <div class="flex-row align-center justify-between mb-16">
      <div class="flex-row align-center">
        <label class="label" style="margin-right: 8px;">篩選儀器</label>
        <el-cascader
          v-model="filterValue"
          style="min-width:260px"
          :options="cascaderOptions"
          :props="cascaderProps"
          :collapse-tags="true"
          :show-all-levels="false"
          placeholder="依區域/測站篩選（不選則顯示全部）"
          clearable
        ></el-cascader>
      </div>
      <el-button
        type="primary"
        :disabled="selectedRows.length === 0"
        @click="openBatchMigrateDialog"
      >
        批量遷移（已選 {{ selectedRows.length }} 筆）
      </el-button>
    </div>
    <el-table
      ref="table"
      :data="filteredInstruments"
      row-key="_id"
      border
      style="width: 100%;"
      @selection-change="handleSelectionChange"
    >
      <el-table-column type="selection" width="55" />
      <el-table-column prop="name" label="儀器名稱" min-width="200" />
      <el-table-column label="操作" width="120">
        <template slot-scope="scope">
          <el-button type="primary" size="mini" @click="openMigrateDialog(scope.row)">
            遷移
          </el-button>
        </template>
      </el-table-column>
    </el-table>

    <el-dialog
      :title="migratingInstruments.length > 1 ? '批量遷移儀器' : '遷移儀器'"
      :visible.sync="dialogVisible"
      width="460px"
    >
      <template v-if="migratingInstruments.length > 0">
        <template v-if="migratingInstruments.length === 1">
          <p>將把儀器「{{ migratingInstruments[0].name }}」遷移到：</p>
        </template>
        <template v-else>
          <p>將把以下 {{ migratingInstruments.length }} 個儀器遷移到：</p>
          <div class="migrate-list mb-16">
            <div v-for="ins in migratingInstruments" :key="ins._id">
              {{ ins.name }}
            </div>
          </div>
        </template>
        <el-select
          v-model="targetProjectId"
          filterable
          placeholder="選擇目標專案"
          style="width: 100%;"
        >
          <el-option
            v-for="proj in targetProjectOptions"
            :key="proj._id"
            :label="proj.project_id"
            :value="proj._id"
          >
            <span style="margin-right:10px;">{{ proj.project_id }}</span>
            <span style="color:#8492a6; font-size:13px;">{{ proj.name }}</span>
          </el-option>
        </el-select>
      </template>
      <span slot="footer" class="dialog-footer">
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" :disabled="!targetProjectId" @click="confirmMigrate">
          確認遷移
        </el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
export default {
  props: {
    project: {
      type: String,
      required: true,
    },
    instrumentList: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      isLoading: false,
      cascaderOptions: [],
      cascaderProps: {
        multiple: true,
        label: 'label',
        value: 'value',
        children: 'children',
      },
      filterValue: [],
      selectedRows: [],
      dialogVisible: false,
      migratingInstruments: [],
      targetProjectId: '',
    };
  },
  computed: {
    projectList() {
      return this.$store.getters['project/projectList'];
    },
    targetProjectOptions() {
      return this.projectList.filter(proj => proj._id !== this.project);
    },
    filteredInstruments() {
      const rows = this.instrumentList.map(ins => ({
        _id: ins._id,
        name: ins.latestVersion?.name,
      }));
      if (this.filterValue.length === 0) return rows;
      const usedIds = this.filterValue.map(path => path[path.length - 1]);
      return rows.filter(row => usedIds.includes(row._id));
    },
  },
  watch: {
    project() {
      this.loadCascaderOptions();
    },
  },
  mounted() {
    this.loadCascaderOptions();
  },
  methods: {
    loadCascaderOptions() {
      this.cascaderOptions = [];
      this.filterValue = [];
      if (!this.project) return;
      this.isLoading = true;
      return this.$axios({
        category: 'project',
        method: 'get',
        apiName: `${this.project}/area-station-instrument`,
      })
        .then(res => {
          this.cascaderOptions = res.data.area.map(area => ({
            label: area.name,
            disabled: area.station.length === 0,
            children: area.station.map(station => ({
              label: station.name,
              disabled: station.instrument.length === 0,
              children: station.instrument.map(instrument => ({
                label: instrument.latestVersion?.name,
                value: instrument._id,
              })),
            })),
          }));
        })
        .catch(this.$notifyError)
        .finally(() => {
          this.isLoading = false;
        });
    },
    handleSelectionChange(rows) {
      this.selectedRows = rows;
    },
    openMigrateDialog(row) {
      this.migratingInstruments = [row];
      this.targetProjectId = '';
      this.dialogVisible = true;
    },
    openBatchMigrateDialog() {
      if (this.selectedRows.length === 0) return;
      this.migratingInstruments = [...this.selectedRows];
      this.targetProjectId = '';
      this.dialogVisible = true;
    },
    async confirmMigrate() {
      const targetProject = this.targetProjectOptions.find(proj => proj._id === this.targetProjectId);
      const targetLabel = targetProject?.project_id || '';
      const count = this.migratingInstruments.length;
      const response = await this.$confirmEdit(
        count === 1
          ? `確認要將儀器「${this.migratingInstruments[0].name}」遷移到「${targetLabel}」`
          : `確認要將這 ${count} 個儀器遷移到「${targetLabel}」`,
      );
      if (response !== 'confirm') return;
      this.isLoading = true;
      const targets = this.migratingInstruments;
      const results = await Promise.allSettled(
        targets.map(ins =>
          this.$axios({
            category: 'admin',
            method: 'put',
            apiName: `instrument/${ins._id}/migrateProject`,
            data: { targetProjectId: this.targetProjectId },
          }),
        ),
      );
      const failed = results
        .map((result, index) => ({ result, instrument: targets[index] }))
        .filter(({ result }) => result.status === 'rejected');

      if (failed.length === 0) {
        this.$notifySuccess(count === 1 ? '儀器已遷移' : `已成功遷移 ${count} 個儀器`);
      } else {
        const failedNames = failed.map(({ instrument }) => instrument.name).join('、');
        this.$notifyError(`成功 ${count - failed.length}/${count} 筆，失敗：${failedNames}`);
      }

      this.dialogVisible = false;
      this.$refs.table.clearSelection();
      await this.$store.dispatch('data/getProjectInstrumentListWithLatestVersion', {
        projectId: this.targetProjectId,
        isUpdate: true,
      });
      this.$emit('migrated');
      this.isLoading = false;
    },
  },
};
</script>

<style lang="scss" scoped>
.migrate-list {
  max-height: 200px;
  overflow-y: auto;
  border: 1px solid #ebeef5;
  border-radius: 4px;
  padding: 8px 12px;
}
</style>
